import React, { useState, forwardRef, useImperativeHandle } from "react";
import styled from "styled-components";
import { useSelector, useDispatch } from "react-redux";

import { IoIosCloseCircle } from "react-icons/io";

import { EditPaneTab } from "./tab";
import { PersonalInfo } from "./personal-info";
import { MovementInfo } from "./movement-info";
import { PrivilegeRoleInfo } from "./privilege-role";
import { CompanyInfo } from "./company-info";
import { CreateMovementForm } from "../movement/create-movement";
import { employeeSchema } from "../schema/personal-schema";
import { employeeMovementSchema } from "../schema/movement-schema";

import { employeeAction } from "../../../actions";
import { employeeSelector } from "../../../selectors";
import { CropImageModal } from "../../modal/crop-image-modal";
import { selectList } from "../types";
import { get } from "lodash";

export const EditPane = React.memo(
  forwardRef((props, ref) => {
    const {
      emp,
      movementData,
      movements,
      _companys,
      _lobs,
      _positions,
      _departments,
      _divisions,
      _teams,
      employeeList,
      setEmp,
      setMovementData,
      openDetail,
      handleSavePersonal,
      handleSaveCompany,
      handleSaveMovement,
      handleSavePrivilegeRole,
      handleClickDelete,
      handleClickClose,
      handleClickCancel,
      handleClickEditTab,
      handleUploadImage,
      handleDeleteMovement,
      handleCreateMovement,
    } = props;
    const dispatch = useDispatch();

    const [activeTab, setActiveTab] = useState(null);
    const [uploadImage, setUploadImage] = useState({});
    const [invalid, setInvalid] = useState({});
    const [invalidModule, setInvalidModule] = useState(false);
    const [isCreate, setIsCreate] = useState(false);

    const id = emp._id;
    const employeeState = useSelector(employeeSelector.getEmployee);
    const isEditPersonal = employeeState.isEditPersonal;
    const isEditCompany = employeeState.isEditCompany;
    const isEditRole = employeeState.isEditRole;
    const isEditMovement = employeeState.isEditMovement;

    useImperativeHandle(ref, () => ({
      resetEditState() {
        Promise.all([
          dispatch(employeeAction.setEditPersonal({ isEditPersonal: false })),
          dispatch(employeeAction.setEditCompany({ isEditCompany: false })),
          dispatch(employeeAction.setEditRole({ isEditRole: false })),
          dispatch(employeeAction.setEditMovement({ isEditMovement: false })),
        ]);
        setActiveTab(null);
      },
      setDefaultTab() {
        setActiveTab(0);
      },
      closeUploadModal() {
        handleCloseUpload();
      },
      resetInvalidList() {
        setInvalid({});
      },
    }));

    const handleClickTab = (tab) => {
      handleClickEditTab(() => {
        setActiveTab(tab);
        setInvalid({});
      });
    };

    const handleClickEdit = (tab) => {
      if (tab === 0) {
        dispatch(employeeAction.setEditPersonal({ isEditPersonal: true }));
      } else if (tab === 1) {
        dispatch(employeeAction.setEditCompany({ isEditCompany: true }));
      } else if (tab === 2) {
        dispatch(employeeAction.setEditMovement({ isEditMovement: true }));
      } else if (tab === 3) {
        dispatch(employeeAction.setEditRole({ isEditRole: true }));
      }
    };

    const handleInputPersonalData = (e, name) => {
      if (selectList.indexOf(name) > -1) {
        setEmp({ ...emp, [name]: e.value });
      } else if (name === "profile_image") {
        e.preventDefault();
        let files;
        if (e.dataTransfer) {
          files = e.dataTransfer.files;
        } else if (e.target) {
          files = e.target.files;
        }
        const reader = new FileReader();
        reader.onload = () => {
          setUploadImage({ open: true, src: reader.result });
        };
        reader.readAsDataURL(files[0]);
      } else if (name === "adjust_after_complete") {
        setEmp({ ...emp, [name]: e.target.checked });
      } else {
        setEmp({ ...emp, [name]: e });
      }
      if (
        name !== "profile_image" &&
        employeeSchema[name].required &&
        (e === "" || (e && e.value === "") || e === null)
      ) {
        setInvalid({ ...invalid, [name]: true });
      } else if (
        name !== "profile_image" &&
        invalid[name] &&
        !(e === "" || e.value === "")
      ) {
        const _invalid = { ...invalid };
        delete _invalid[name];
        setInvalid(_invalid);
      }
    };

    const handleInputMovementData = (e, name) => {
      if (name === "module") {
        const regex = /[a-z]/g;
        const string = get(e, 'target.value', '')
        const replace = string.replace(regex, "");
        if (replace !== "") {
          setInvalidModule(true)
        } else {
          setInvalidModule(false)
        }
        setMovementData({ ...movementData, [name]: e.target.value });
      } else if (name === "company_id") {
        setMovementData({
          ...movementData,
          [name]: e.value,
          lob_id: "",
          department_id: "",
        });
      } else if (name === "lob_id") {
        setMovementData({
          ...movementData,
          [name]: e.value,
          department_id: "",
        });
      } else if (selectList.indexOf(name) > -1) {
        setMovementData({ ...movementData, [name]: e.value });
      } else {
        setMovementData({ ...movementData, [name]: e });
      }
      if (
        employeeMovementSchema[name].required &&
        (e === "" || (e && e.value === "") || e === null)
      ) {
        setInvalid({ ...invalid, [name]: true });
      } else if (invalid[name] && !(e === "" || e.value === "")) {
        const _invalid = { ...invalid };
        delete _invalid[name];
        if (name === "hiring_date") {
          delete _invalid["hiring_date"];
        }
        setInvalid(_invalid);
      }
    };

    const handleCheckbox = (e) => {
      setEmp({ ...emp, [e.target.name]: e.target.checked });
    };

    const handleClickSavePersonal = () => {
      handleSavePersonal(emp);
    };

    const handleClickSaveMovement = () => {
      handleSaveMovement(movementData);
    };

    const handleChangeMovement = (e) => {
      setMovementData({ ...e });
    };

    const handleClickCreateMovement = (payload) => {
      handleCreateMovement(payload);
      setIsCreate(false);
    };

    const handleCancel = (id) => {
      handleClickCancel(id);
    };

    const handleCloseUpload = () => {
      const uploadImage = document.getElementById("profile_image");
      uploadImage.value = null;
      setUploadImage({ open: false });
    };

    return (
      <Root openDetail={openDetail}>
        <CropImageModal
          src={uploadImage.src}
          isOpen={uploadImage.open}
          userID={emp && emp._id}
          handleUpload={handleUploadImage}
          handleClose={handleCloseUpload}
        />
        <CreateMovementForm
          _lobs={_lobs}
          _teams={_teams}
          _companys={_companys}
          _positions={_positions}
          _divisions={_divisions}
          _departments={_departments}
          isCreate={isCreate}
          users={employeeList}
          movementData={movementData}
          isEditCompany={isEditCompany}
          setIsCreate={() => setIsCreate(false)}
          handleClickCreate={handleClickCreateMovement}
        />
        <ModalHeader>
          <EditPaneTab
            activeTab={activeTab}
            handleClickTab={handleClickTab}
            handleClickDelete={() => handleClickDelete(id)}
          />
          <IoIosCloseCircle
            onClick={handleClickClose}
            style={{ color: "#C4C4C4" }}
          />
        </ModalHeader>
        {emp && Object.keys(emp).length > 0 && activeTab === 0 && (
          <PersonalInfo
            emp={emp}
            invalid={invalid}
            employeeList={employeeList}
            editPersonal={isEditPersonal}
            handleClickCancel={handleCancel}
            handleClickEdit={handleClickEdit}
            handleInput={handleInputPersonalData}
            handleClickSavePersonal={handleClickSavePersonal}
          />
        )}
        {movementData &&
          Object.keys(movementData).length > 0 &&
          activeTab === 1 && (
            <CompanyInfo
              emp={emp}
              invalid={invalid}
              users={employeeList}
              isEditCompany={isEditCompany}
              handleClickCancel={handleCancel}
              handleClickEdit={handleClickEdit}
              handleInput={handleInputPersonalData}
              handleSaveCompany={handleSaveCompany}
            />
          )}
        {emp && Object.keys(emp).length > 0 && activeTab === 2 && (
          <MovementInfo
            emp={emp}
            _lobs={_lobs}
            _teams={_teams}
            _companys={_companys}
            _divisions={_divisions}
            _positions={_positions}
            _departments={_departments}
            invalid={invalid}
            invalidModule={invalidModule}
            movements={movements}
            movementData={movementData}
            isEditMovement={isEditMovement}
            handleCheckbox={handleCheckbox}
            handleClickCancel={handleCancel}
            handleClickEdit={handleClickEdit}
            handleInput={handleInputMovementData}
            handleDeleteMovement={handleDeleteMovement}
            handleClickSaveMovement={handleClickSaveMovement}
            handleChangeMovement={handleChangeMovement}
            handleClickNewMovement={() => setIsCreate(true)}
          />
        )}
        {emp && Object.keys(emp).length > 0 && activeTab === 3 && (
          <PrivilegeRoleInfo
            isEditRole={isEditRole}
            emp={emp}
            handleCheckbox={handleCheckbox}
            handleClickCancel={handleCancel}
            handleClickEdit={handleClickEdit}
            handleSavePrivilegeRole={handleSavePrivilegeRole}
          />
        )}
      </Root>
    );
  })
);

const Root = styled.div`
  display: flex;
  flex-direction: column;
  flex: ${(p) => (p.openDetail ? 1 : 0)};
  width: ${(p) => (p.openDetail ? "auto" : 0)};
  margin-left: ${(p) => (p.openDetail ? "12px" : 0)};
  transition: all 0.3s;
`;

const ModalHeader = styled.div`
  width: 100%;
  height: 42px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f9f9f9;
  padding: 12px 24px;

  svg {
    font-size: 24px;
    color: #616161;
    cursor: pointer;
  }
`;



// WEBPACK FOOTER //
// ./src/components/new-employee/edit-pane/index.js